/*
 * PDP Surgical — minimal overlay JS.
 *
 * The existing PDP pipeline (sku-picker.js / style-picker / variant-picker)
 * remains the source of truth for size, framing, border, frame-color, price.
 *
 * This file only adds:
 *   1. Sticky CTA visibility — IntersectionObserver on .buy-buttons-block.
 *      Mirrors product title/config/price into the sticky bar.
 *   2. Floating Art Advisor chip — appears after 280px scroll.
 *   3. [data-advisor-open] click hooks — dispatch whisper:open-chat with
 *      focusedPiece + userAction so the gallery-wall DiscoverApp opens in a
 *      modal drawer and the skills router engages art_design / frame_design
 *      instead of the default wall_curation. The bridge listener lives in
 *      assets/whisper-block.js (openDiscoverDrawer + handoff staging).
 *
 * Idempotent — safe to run multiple times (Shopify section reloads).
 */

import { getTasteIdentity } from '@theme/for-you-recommendations';

(() => {
  'use strict';

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // ============================================================
  // STICKY CTA — track variant-picker / style-picker state
  // ============================================================
  function initStickyCta(signal) {
    const sticky   = $('[data-pdp-sticky]');
    const buyBlock = $('.buy-buttons-block, .product-form-buttons');
    if (!sticky || !buyBlock) return;

    const obs = new IntersectionObserver(
      ([e]) => {
        const visible = !e.isIntersecting && e.boundingClientRect.top < 0;
        sticky.classList.toggle('visible', visible);
        sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
        // Hide the floating advisor chip whenever the sticky CTA is up —
        // they share the bottom-right corner and would otherwise overlap.
        const chip = $('[data-pdp-aa-chip]');
        if (chip) chip.classList.toggle('pdp-aa-chip--sticky-visible', visible);
      },
      { threshold: 0 }
    );
    obs.observe(buyBlock);
    signal?.addEventListener('abort', () => obs.disconnect(), { once: true });

    // Sticky "Add" click is wired in pdp-surgical-config.js#initStickyCta.
    // Binding it here too caused a duplicate native-button click → two
    // POSTs to /cart/add → cart line +2 on every sticky ATC press.

    // Sync sticky bar with current state — read the live UI values
    const updateStickyContent = () => {
      const titleEl = $('.product-title-h1, .product-details h1');
      const stickyTitle = sticky.querySelector('.title');
      if (titleEl && stickyTitle) stickyTitle.textContent = titleEl.textContent.trim();

      // Read current price from .price-current (post-tier override)
      const priceEl = $('.price-current');
      const wasEl   = $('.price-compare-at');
      const stickySub = $('[data-pdp-sticky-sub]', sticky);
      if (stickySub) {
        const size = new URL(location.href).searchParams.get('size') || '';
        // The size param is attacker-controlled (shareable URL) — escape it
        // and every other interpolated string before innerHTML.
        const sizeFmt = esc(size.replace(/-x-/g, ' × ').replace(/-/g, ' '));
        const frame = esc($('.style-picker__header-value')?.textContent.trim() || '');
        const now   = priceEl ? `<span class="now">${esc(priceEl.textContent.trim())}</span>` : '';
        const was   = wasEl ? `<span class="was">${esc(wasEl.textContent.trim())}</span>` : '';
        stickySub.innerHTML = `${sizeFmt}${frame ? ', ' + frame : ''} · ${now} ${was}`;
      }
    };

    updateStickyContent();

    // Listen for any variant/configurator change event
    const events = [
      'variant:change',
      'sku-picker:selection-changed',
      'sku-picker:variant-changed',
      'style-picker:applied',
      'gallery-wall:config-changed',
      'size-impact:changed',
    ];
    events.forEach((evt) => {
      document.addEventListener(evt, updateStickyContent, { signal });
    });
  }

  // ============================================================
  // ART ADVISOR — entry point binding + floating chip
  // ============================================================
  function getProductHandle() {
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : location.pathname.split('/').pop();
  }

  function getFocusedPiece() {
    const handle = getProductHandle();
    if (!handle) return null;
    // Walk specific selectors in priority order — querySelector with a
    // comma list matches in DOM order rather than selector-list order, so
    // a generic [data-product-title] attribute (e.g. on a wishlist button)
    // can win over the actual H1 if it appears earlier in the document.
    const titleSelectors = [
      '.product-title-h1',
      '.rmf-title',
      '.product-details h1',
      'h1[data-product-title]',
      'main h1',
    ];
    let titleEl = null;
    for (const sel of titleSelectors) {
      titleEl = $(sel);
      if (titleEl) break;
    }
    const title = titleEl?.textContent.trim() || null;
    // .rmf-title contains an italicised <span class="rmf-title__sub"> suffix
    // separated by an em-dash. textContent collapses it; that's the full
    // product title and reads correctly when echoed back to the user.
    const artistEl = $('[data-product-artist], .product-artist, .artist-name');
    const imgEl = $(
      'product-media img, .product-image img, [data-product-featured-image], .rmf-hero img, .rmf-preview img'
    );
    return {
      id: handle,
      handle,
      title,
      artist: artistEl?.textContent.trim() || null,
      imageUrl: imgEl?.currentSrc || imgEl?.src || null,
      type: 'catalog',
      openedFrom: 'pdp',
    };
  }

  // Read the photo currently staged in sessionStorage by photo-upload.js.
  // Returns null when no photo is uploaded for this session.
  function getUploadedPhoto() {
    try {
      const raw = sessionStorage.getItem('uploaded_photo');
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (!p?.dataUrl) return null;
      return {
        dataUrl: p.dataUrl,
        fileName: p.fileName || 'photo',
        fileType: p.fileType || 'image/jpeg',
        width: p.width || null,
        height: p.height || null,
      };
    } catch {
      return null;
    }
  }

  // Visibly flash the photo-upload row so the user knows where to act when
  // they click a photo-gated advisor callout without an uploaded photo.
  function nudgeToPhotoUpload() {
    const row = document.querySelector('[data-photo-upload-row], photo-upload-component, .photo-upload-inline');
    if (!row) return;
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    row.classList.add('photo-upload-flash');
    setTimeout(() => row.classList.remove('photo-upload-flash'), 1400);
  }

  function openAdvisor(intent, opts = {}) {
    let focusedPiece = opts.focusedPiece || getFocusedPiece();
    // includePhoto defaults to true for back-compat with callers that
    // explicitly want the photo (e.g. rmf advisor). bindAdvisorOpeners
    // sets this from the callout's data-photo-gated attribute.
    const includePhoto = opts.includePhoto !== false;
    const uploadedPhoto = includePhoto ? (opts.uploadedPhoto || getUploadedPhoto()) : null;

    // RMF "Let Advisor design your frame" semantics — the *artwork* is the
    // user's uploaded photo, and the PDP they landed on is the *frame
    // product*. focusedPiece in agent-speak means "the piece being framed",
    // so the photo takes that slot; the frame product moves to a new
    // `framingProductHandle` field so the advisor still knows which RM
    // range was the entry point (soft preference, apply-target hint).
    // Without this, focusedPiece = the frame, the prompt reads "the
    // artwork is The Beat Frame — Gold" and the model frames the frame.
    let framingProductHandle = null;
    if (intent === 'frame_my_photo' && uploadedPhoto && (uploadedPhoto.url || uploadedPhoto.dataUrl)) {
      const photoId = uploadedPhoto.photoId || uploadedPhoto.fileName || 'photo';
      framingProductHandle = focusedPiece?.handle || getProductHandle() || null;
      focusedPiece = {
        type: 'upload',
        handle: `upload:${photoId}`,
        title: 'Your photo',
        imageUrl: uploadedPhoto.url || uploadedPhoto.dataUrl || null,
        artist: null,
        openedFrom: 'rmf-pdp',
      };
    }

    const detail = {
      intent: intent || 'general',
      userAction: opts.prompt || null,
      productHandle: focusedPiece?.handle || getProductHandle() || null,
      focusedPiece,
      uploadedPhoto,
      framingProductHandle,
    };
    if (opts.greeting) {
      detail.whisper = { text: opts.greeting };
    }

    // Bridge in assets/whisper-block.js stages the handoff in sessionStorage
    // and opens the DiscoverDrawer (lazy-loads gallery-wall.js). The bridge
    // is responsible for uploading any photoDataUrl to S3 before the SPA
    // mounts so it can pass a fetchable URL to the agent.
    document.dispatchEvent(new CustomEvent('whisper:open-chat', { detail }));
  }

  function bindAdvisorOpeners(root = document) {
    $$('[data-advisor-open]', root).forEach((el) => {
      if (el.dataset.pdpAdvisorBound === '1') return;
      el.dataset.pdpAdvisorBound = '1';
      el.addEventListener('click', (ev) => {
        ev.preventDefault();
        const intent = el.dataset.advisorIntent || 'general';

        // Photo-gated callouts (Mode A): without a photo, scroll + flash the
        // upload row instead of opening an advisor that has nothing to work
        // with. The advisor agent's frame_design / art_design skills both
        // need an image input.
        const photoGated = el.dataset.photoGated === 'true';
        if (photoGated && !getUploadedPhoto()) {
          nudgeToPhotoUpload();
          return;
        }

        // Only carry uploadedPhoto into the handoff for photo-gated
        // callouts (currently: rmf PDP). Other surfaces (surgical art PDP,
        // mood-prints, complete-the-wall, etc.) shouldn't surface a stray
        // sessionStorage photo from a previous rmf visit — that flips the
        // skill router from frame_design to art_design unintentionally.
        openAdvisor(intent, {
          greeting: el.dataset.advisorGreeting,
          prompt: el.dataset.advisorPrompt,
          includePhoto: photoGated,
        });
      });
    });
  }

  // Toggle body[data-photo-uploaded] so CSS can swap the gated states of
  // [data-photo-gated] callouts. Handles initial state + the photo-upload
  // component's lifecycle events.
  function syncPhotoUploadedAttr() {
    const has = !!getUploadedPhoto();
    if (has) {
      document.body.setAttribute('data-photo-uploaded', '');
    } else {
      document.body.removeAttribute('data-photo-uploaded');
    }
  }
  document.addEventListener('photo-upload:changed', syncPhotoUploadedAttr);
  document.addEventListener('photo-upload:removed', syncPhotoUploadedAttr);
  // Sync on storage events too — covers the case where the same photo state
  // is in another tab / popped via back navigation.
  window.addEventListener('storage', (e) => {
    if (e.key === 'uploaded_photo' || e.key === null) syncPhotoUploadedAttr();
  });

  function initFloatingChip(signal) {
    const chip = $('[data-pdp-aa-chip]');
    if (!chip) return;
    if (sessionStorage.getItem('pdp_advisor_chip_dismissed') === '1') return;

    const onScroll = () => {
      chip.classList.toggle('visible', window.scrollY > 280);
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    onScroll();

    chip.addEventListener('click', (ev) => {
      if (ev.target.closest('[data-pdp-aa-chip-dismiss]')) {
        ev.stopPropagation();
        sessionStorage.setItem('pdp_advisor_chip_dismissed', '1');
        chip.classList.remove('visible');
        chip.style.display = 'none';
        return;
      }
      openAdvisor('chip');
    });
  }

  // ============================================================
  // FRAME DRAWER — open when style-picker's "View all" or
  // "Build your own" link is clicked. Reuses the existing native
  // <dialog> path that style-picker.js already triggers, OR
  // falls back to our pdp-frame-drawer dialog if the native one
  // isn't present.
  // ============================================================
  function initFrameDrawer() {
    const drawer = $('[data-pdp-frame-drawer]');
    if (!drawer) return;

    // Close handler
    const closeDrawer = () => {
      if (typeof drawer.close === 'function') drawer.close();
      else drawer.removeAttribute('open');
      document.body.style.overflow = '';
    };

    $('[data-pdp-fb-close]', drawer)?.addEventListener('click', closeDrawer);
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) closeDrawer();
    });
    drawer.addEventListener('cancel', () => { document.body.style.overflow = ''; });

    // Tab switching
    $$('[data-pdp-fb-tab]', drawer).forEach((tabBtn) => {
      tabBtn.addEventListener('click', () => {
        const which = tabBtn.dataset.pdpFbTab;
        $$('.tab', drawer).forEach((t) => t.classList.toggle('on', t.dataset.pdpFbTab === which));
        $$('[data-pdp-fb-tabbody]', drawer).forEach((b) => {
          b.hidden = b.dataset.pdpFbTabbody !== which;
        });
      });
    });
  }

  // ============================================================
  // CTW RECOMMENDATIONS — find complementary art for the pair/trio
  // mock frames. Uses for-you API with orientation constrained at
  // retrieval time (not post-filtered). Falls back to Shopify's
  // complementary-intent recommendations for anonymous users.
  // ============================================================

  // Derive portrait/landscape/square from the active size URL param.
  function getProductOrientation() {
    const size = new URLSearchParams(location.search).get('size') || '';
    const match = size.match(/(\d+)-x-(\d+)/);
    if (!match) return null;
    const w = Number(match[1]);
    const h = Number(match[2]);
    if (w > h * 1.15) return 'landscape';
    if (h > w * 1.15) return 'portrait';
    return 'square';
  }

  async function initCtwRecommendations() {
    const ctwSection = $('[data-pdp-ctw]');
    if (!ctwSection) return;

    const mocks = $$('art-set-mock', ctwSection);
    if (!mocks.length) return;

    const handle = location.pathname.split('/products/').pop()?.split('?')[0]?.split('/')[0];
    if (!handle) return;

    const orientation = getProductOrientation();

    // Call for-you directly — getTasteIdentity includes whatever identity is available
    // (customer ID, session fingerprint, or neither). No gate: the server handles anonymous.
    const { customerId, sessionFingerprint } = getTasteIdentity();
    const market = (() => {
      const c = window.Shopify?.country?.toLowerCase() || '';
      if (c === 'us') return 'us';
      if (c === 'au') return 'au';
      if (c === 'ca') return 'ca';
      if (['de','fr','es','it','nl','be','at','pt','ie','fi','gr','lu','mt','cy','sk','si','ee','lv','lt'].includes(c)) return 'eu';
      return 'gb';
    })();

    let companions = [];
    try {
      const r = await fetch(`/a/accounts/for-you?market=${market}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: customerId || undefined,
          sessionFingerprint: sessionFingerprint || undefined,
          context: {
            productHandle: handle,
            orientation: orientation || undefined,
          },
          excludeHandles: [handle],
          hits: 6,
          market,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        const aiHidden = window.Fy?.isAiHidden; // hide-ai-art gate; unset unless gated
        companions = (data.products || [])
          .filter((p) => typeof aiHidden !== 'function' || !aiHidden(p))
          .map((p) => ({ url: p.image_url || p.image?.src || null, title: p.title || '' }))
          .filter((p) => p.url);
      }
    } catch {
      // keep server-rendered artworks (balanced_complements or mock example_artworks)
    }

    if (!companions.length) return;

    mocks.forEach((mock) => {
      const anchor = mock.artworks?.[0] || null;
      mock.artworks = anchor ? [anchor, ...companions] : companions;
      if (typeof mock.renderFrames === 'function') mock.renderFrames();
    });
  }

  // ============================================================
  // BOOT
  // ============================================================
  let bootAbort = null;
  function boot() {
    // One controller per boot cycle: shopify:section:load re-runs boot()
    // against a replaced DOM, and without cleanup each run stacked another
    // IntersectionObserver, window scroll handler, and six document
    // listeners on top of the previous run's.
    bootAbort?.abort();
    bootAbort = new AbortController();
    const { signal } = bootAbort;
    initStickyCta(signal);
    bindAdvisorOpeners();
    initFloatingChip(signal);
    initFrameDrawer();
    syncPhotoUploadedAttr();
    // Defer CTW recommendations until after critical paint
    if (document.readyState === 'complete') {
      initCtwRecommendations();
    } else {
      window.addEventListener('load', initCtwRecommendations, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
